import { NextPage } from "next"
import PageLayout from "../../components/pageLayout"
import { getAllPages } from "../../util/airtable-pages"
import Collections from "../../components/collections"
import Exhibitions from "../../components/exhibitions"
import GroupExhibitions from "../../components/groupExhibitions"
import Recommended from "../../components/recommended"
import Bibliography from "../../components/bibliography"
import NathanielPeriodicals from "../../components/nathanielPeriodicals"
import { getPageData } from "../api/[parent]"
import { FieldSet, Records } from "airtable"
import { base, minifyRecord, minifyRecords } from "../api/utils/airtable"
import { PageFields } from "."
import { NextSeo } from "next-seo"
import GrantsIndex from "../../components/grants"

export enum Routes {
  Foundation = "foundation",
  Artist = "artist",
  Resources = "resources",
  Features = "features",
}

export enum ResourcesRoutes {
  Exhibitions = "exhibitions",
  GroupExhibitions = "group-exhibitions",
  Collections = "collections",
  Recommended = "recommended",
  Bibliography = "bibliography",
  RichardPapers = "richard-papers",
  FloraPapers = "flora-papers",
  Grants = "grants",
  NathanielPapers = "nathaniel-pousette-dart-papers",
  NathanielPeriodicals = "nathaniel-pousette-dart-periodicals",
}

const Page: NextPage<PageFields> = (props) => {
  const { pageData, contentData } = props

  const { title } = pageData

  const pageSlug = pageData.id
  const pageTitle = title ? title.toString() : ""

  const Layout = () => {
    switch (pageSlug) {
      case ResourcesRoutes.Collections:
        return <PageLayout {...pageData}>{contentData && <Collections contentData={contentData} />}</PageLayout>
      case ResourcesRoutes.GroupExhibitions:
        return <PageLayout {...pageData}>{contentData && <GroupExhibitions contentData={contentData} />}</PageLayout>
      case ResourcesRoutes.Exhibitions:
        return <PageLayout {...pageData}>{contentData && <Exhibitions contentData={contentData} />}</PageLayout>
      case ResourcesRoutes.Recommended:
        return <PageLayout {...pageData}>{contentData && <Recommended contentData={contentData} />}</PageLayout>
      case ResourcesRoutes.Bibliography:
        return <PageLayout {...pageData}>{contentData && <Bibliography contentData={contentData} />}</PageLayout>
      case ResourcesRoutes.NathanielPeriodicals:
        return (
          <PageLayout {...pageData}>{contentData && <NathanielPeriodicals contentData={contentData} />}</PageLayout>
        )
      case ResourcesRoutes.Grants:
        return <PageLayout {...pageData}>{contentData && <GrantsIndex contentData={contentData} />}</PageLayout>
      default:
        return <PageLayout {...pageData} />
    }
  }

  return (
    <>
      <NextSeo title={pageTitle} />
      <Layout />
    </>
  )
}

export default Page

export async function getStaticProps(context: any) {
  try {
    // get the parent slug
    const parentSlug = context.params ? context.params.parent : ""
    const pageSlug = context.params ? context.params.id : ""

    if (!parentSlug || !pageSlug) {
      return { notFound: true }
    }

    const pageData = await getPageData(pageSlug.toString())
    const parentData = await getPageData(parentSlug.toString())

    if (!pageData || (Object.values(Routes) as string[]).includes(pageSlug)) {
      return { notFound: true }
    }

    let contentData: Records<FieldSet> | undefined
    if (pageSlug == ResourcesRoutes.Collections) {
      contentData = await base("Public Collections").select({ view: "All" }).all()
    }
    if (pageSlug == ResourcesRoutes.Recommended) {
      contentData = await base("Recommended").select({ view: "Published" }).all()
    }
    if (pageSlug == ResourcesRoutes.Bibliography) {
      contentData = await base("Selected Bibliography").select({ view: "All" }).all()
    }
    if (pageSlug == ResourcesRoutes.GroupExhibitions) {
      contentData = await base("Group Artist Exhibitions").select({ view: "All" }).all()
    }
    if (pageSlug == ResourcesRoutes.Exhibitions) {
      contentData = await base("Single Artist Exhibitions").select({ view: "All" }).all()
    }
    if (pageSlug == ResourcesRoutes.NathanielPeriodicals) {
      contentData = await base("Nathaniel Pousette-Dart Periodicals").select({ view: "Published" }).all()
    }

    if (pageSlug == ResourcesRoutes.Grants) {
      contentData = await base("Grants").select({ view: "All" }).all()
    }

    return {
      props: {
        pageData: minifyRecord(pageData).fields,
        parentData: parentData ? parentData.fields : null,
        contentData: contentData ? minifyRecords(contentData) : null,
      },
    }
  } catch (err) {}
}

export const getStaticPaths = async () => {
  const pages = await getAllPages()
  const paths = pages.map((page) => {
    return {
      params: { parent: page.params.parent_slug[0], id: page.params.id },
    }
  })

  return {
    paths: paths,
    fallback: false,
  }
}
